import { supabase, isConfigured } from './supabase';
import type { SalesUserSettings, AssignmentRule, AssignmentRuleConditions } from '../types';

// ============================================================
// USER SETTINGS (Booking URL, Demo Env, Timezone)
// ============================================================

export async function getUserSettings(userId: string): Promise<SalesUserSettings | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_user_settings')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') {
    console.error('[settings] getUserSettings failed:', error);
  }

  return data as SalesUserSettings | null;
}

export async function upsertUserSettings(
  userId: string,
  teamId: string,
  settings: Partial<Pick<SalesUserSettings, 'booking_url' | 'demo_env_url' | 'timezone'>>
): Promise<SalesUserSettings | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_user_settings')
    .upsert({
      user_id: userId,
      team_id: teamId,
      ...settings,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('[settings] upsertUserSettings failed:', error);
    return null;
  }

  return data as SalesUserSettings;
}

// ============================================================
// ASSIGNMENT RULES
// ============================================================

export async function getAssignmentRules(teamId: string): Promise<AssignmentRule[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('sales_assignment_rules')
    .select('*')
    .eq('team_id', teamId)
    .order('priority', { ascending: true });

  if (error) {
    console.error('[settings] getAssignmentRules failed:', error);
    return [];
  }

  return data as AssignmentRule[];
}

export async function createAssignmentRule(
  teamId: string,
  name: string,
  conditions: AssignmentRuleConditions,
  assignToUserId: string,
  priority = 0
): Promise<AssignmentRule | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_assignment_rules')
    .insert({
      team_id: teamId,
      name,
      priority,
      conditions,
      assign_to_user_id: assignToUserId,
      enabled: true,
    })
    .select()
    .single();

  if (error) {
    console.error('[settings] createAssignmentRule failed:', error);
    return null;
  }

  return data as AssignmentRule;
}

export async function updateAssignmentRule(
  id: string,
  updates: Partial<Pick<AssignmentRule, 'name' | 'conditions' | 'assign_to_user_id' | 'priority' | 'enabled'>>
): Promise<AssignmentRule | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_assignment_rules')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[settings] updateAssignmentRule failed:', error);
    return null;
  }

  return data as AssignmentRule;
}

export async function deleteAssignmentRule(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_assignment_rules')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[settings] deleteAssignmentRule failed:', error);
    return false;
  }

  return true;
}

export async function applyAssignmentRules(contactId: string): Promise<string | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase.rpc('apply_assignment_rules', {
    p_contact_id: contactId,
  });

  if (error) {
    console.error('[settings] applyAssignmentRules failed:', error);
    return null;
  }

  return data as string | null;
}

// ============================================================
// BOOKING LINK MESSAGE
// ============================================================

export function generateBookingMessage(
  bookingUrl: string,
  contactName: string,
  dealTitle?: string
): string {
  let message = `Hi ${contactName},\n\n`;
  message += `I'd love to show you how ReHub can help your business.\n\n`;
  if (dealTitle) {
    message += `Regarding "${dealTitle}", please book a time that works for you:\n`;
  } else {
    message += `Please book a time that works for you:\n`;
  }
  message += `${bookingUrl}\n\n`;
  message += `Looking forward to speaking with you!\n`;
  return message;
}
